import type { PresentationQualityTier } from './clientPerformance';

export interface PresentationWindow {
  p95Ms: number;
  p99Ms: number;
  longTasks: number;
  targetFrameMs?: number;
}

export interface PresentationGovernorOptions {
  downgradeWindows?: number;
  recoveryWindows?: number;
  cooldownMs?: number;
}

export interface PresentationGovernorDecision {
  tier: PresentationQualityTier;
  reason: string;
  changed: boolean;
}

const TIERS: readonly PresentationQualityTier[] = ['full', 'reduced', 'constrained'];
const DEFAULT_TARGET_FRAME_MS = 1_000 / 60;

export class PresentationGovernor {
  private tier: PresentationQualityTier = 'full';
  private minimumTier: PresentationQualityTier = 'full';
  private pressureWindows = 0;
  private stableWindows = 0;
  private lastChangeAt = Number.NEGATIVE_INFINITY;

  constructor(private readonly options: PresentationGovernorOptions = {}) {}

  currentTier(): PresentationQualityTier {
    return this.tier;
  }

  reset(nowMs: number): PresentationGovernorDecision {
    this.tier = this.minimumTier;
    this.pressureWindows = 0;
    this.stableWindows = 0;
    this.lastChangeAt = nowMs;
    return {
      tier: this.tier,
      reason: 'run-reset',
      changed: true,
    };
  }

  setMinimumTier(
    tier: PresentationQualityTier,
    reason: string,
    nowMs: number,
  ): PresentationGovernorDecision {
    this.minimumTier = tier;
    this.pressureWindows = 0;
    this.stableWindows = 0;
    if (TIERS.indexOf(this.tier) < TIERS.indexOf(tier)) {
      this.tier = tier;
      this.lastChangeAt = nowMs;
      return { tier: this.tier, reason, changed: true };
    }
    return { tier: this.tier, reason, changed: false };
  }

  observe(window: PresentationWindow, nowMs: number): PresentationGovernorDecision {
    const targetFrameMs =
      window.targetFrameMs !== undefined &&
      Number.isFinite(window.targetFrameMs) &&
      window.targetFrameMs > 0
        ? window.targetFrameMs
        : DEFAULT_TARGET_FRAME_MS;
    const pressured =
      this.tier === 'full'
        ? window.p95Ms > targetFrameMs * 1.2 ||
          window.p99Ms > targetFrameMs * 2.25 ||
          window.longTasks > 0
        : window.p95Ms > targetFrameMs * 2 ||
          window.p99Ms > targetFrameMs * 3 ||
          window.longTasks > 0;
    const stable =
      window.longTasks === 0 &&
      (this.tier === 'reduced'
        ? window.p95Ms <= targetFrameMs * 1.1 &&
          window.p99Ms <= targetFrameMs * 2.1
        : this.tier === 'constrained'
          ? window.p95Ms <= targetFrameMs * 1.65 &&
            window.p99Ms <= targetFrameMs * 2.5
          : false);
    const coolingDown = nowMs - this.lastChangeAt < (this.options.cooldownMs ?? 5_000);
    if (coolingDown) {
      this.pressureWindows = 0;
      this.stableWindows = 0;
      return { tier: this.tier, reason: 'cooldown', changed: false };
    }
    this.pressureWindows = pressured ? this.pressureWindows + 1 : 0;
    this.stableWindows = stable ? this.stableWindows + 1 : 0;
    if (this.pressureWindows >= (this.options.downgradeWindows ?? 3)) {
      const index = TIERS.indexOf(this.tier);
      if (index < TIERS.length - 1) {
        this.tier = TIERS[index + 1]!;
        this.pressureWindows = 0;
        this.stableWindows = 0;
        this.lastChangeAt = nowMs;
        return {
          tier: this.tier,
          reason: 'sustained-frame-pressure',
          changed: true,
        };
      }
    }
    if (this.stableWindows >= (this.options.recoveryWindows ?? 6)) {
      const index = TIERS.indexOf(this.tier);
      const minimumIndex = TIERS.indexOf(this.minimumTier);
      if (index > minimumIndex) {
        this.tier = TIERS[index - 1]!;
        this.pressureWindows = 0;
        this.stableWindows = 0;
        this.lastChangeAt = nowMs;
        return {
          tier: this.tier,
          reason: 'sustained-recovery',
          changed: true,
        };
      }
      this.stableWindows = 0;
    }
    return { tier: this.tier, reason: pressured ? 'observing-frame-pressure' : 'stable', changed: false };
  }
}
